import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSearchParams } from 'react-router-dom';

import * as ssoApi from '../../api/sso';
import type { SsoBrokerStatus } from '../../api/sso';
import * as tenantsApi from '../../api/tenants';
import type { Tenant } from '../../api/tenants';
import { extractErrorMessage } from '../../api/errors';
import { SearchableSelect } from '../../components/SearchableSelect';
import { SsoConnectionPanel } from './SsoConnectionPanel';

/**
 * Enterprise SSO settings (superadmin only) — the Configuration-page home
 * for ENTERPRISE-SSO.
 *
 * SSO config is inherently *per tenant* (a connection maps email domains to
 * one tenant's IdP), so this tab is a tenant picker in front of the same
 * `SsoConnectionPanel` the Tenant Detail page renders. It exists because
 * that page is the only other host, and the only way in is a click on a
 * Dashboard tenant card — so an admin looking under Configuration for the
 * SSO switches found nothing and reasonably concluded the feature was gone.
 *
 * The picked tenant lives in the URL (`?tab=sso&tenant=<id>`) so a
 * particular tenant's connection is linkable and survives a reload.
 *
 * Above the picker sits the broker status, which is the other half of the
 * setup and lives somewhere else entirely: the API's WorkOS credentials
 * (env / SSM), not the portal. A connection saved here does nothing until
 * those are present, and that asymmetry is invisible without saying so.
 */
export function SsoSettings({
  embedded = false,
}: { embedded?: boolean } = {}) {
  const [params, setParams] = useSearchParams();
  const selected = params.get('tenant') ?? '';

  const [tenants, setTenants] = useState<Tenant[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [broker, setBroker] = useState<SsoBrokerStatus | null>(null);

  useEffect(() => {
    let alive = true;
    tenantsApi
      .listTenants()
      .then((rows) => {
        if (!alive) return;
        setTenants(
          [...rows].sort((a, b) => a.name.localeCompare(b.name)),
        );
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setLoadError(extractErrorMessage(err, 'failed to load tenants'));
        setTenants([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    // A failure here is not worth an error banner — the panel below still
    // works, we just can't say whether the broker is live.
    ssoApi
      .getSsoBrokerStatus()
      .then((s) => {
        if (alive) setBroker(s);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  const pickTenant = (id: string) => {
    // Preserve `tab=sso` (and anything else) — only swap the tenant.
    const next = new URLSearchParams(params);
    if (id) next.set('tenant', id);
    else next.delete('tenant');
    setParams(next, { replace: true });
  };

  return (
    <Stack spacing={3}>
      {!embedded && (
        <Stack>
          <Typography variant="h4">Enterprise SSO</Typography>
          <Typography variant="body2" color="text.secondary">
            Wire a tenant to its identity provider through the SSO broker.
          </Typography>
        </Stack>
      )}

      {broker !== null &&
        (broker.configured ? (
          <Alert severity="success">
            SSO broker is configured on this API. Callback URL:{' '}
            <code>{broker.redirect_uri}</code> — it must match the redirect
            registered in the WorkOS dashboard.
          </Alert>
        ) : (
          <Alert severity="warning">
            <strong>The SSO broker is not configured on this API</strong>, so
            every sign-in falls back to a password regardless of what is set
            below. It needs <code>WORKOS_API_KEY</code>,{' '}
            <code>WORKOS_CLIENT_ID</code> and <code>SSO_REDIRECT_URI</code> in
            the API environment (SSM in staging) — the portal cannot set
            those. Connections saved here start working the moment they are
            present.
          </Alert>
        ))}

      {loadError && <Alert severity="error">{loadError}</Alert>}

      {tenants === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <>
          <SearchableSelect
            id="sso-tenant"
            label="Tenant"
            size="medium"
            value={tenants.some((t) => t.id === selected) ? selected : ''}
            onChange={pickTenant}
            options={tenants.map((t) => ({ value: t.id, label: t.name }))}
            sx={{ maxWidth: 360 }}
            disabled={tenants.length === 0}
            helperText={
              tenants.length === 0
                ? 'No tenants yet — create one first.'
                : 'Each connection claims its email domains for one tenant.'
            }
          />

          {selected && tenants.some((t) => t.id === selected) ? (
            /* `key` remounts the panel per tenant: its form state hydrates
               on load, so without this, switching from a configured tenant
               to one with no connection (a 404) would leave the previous
               tenant's domains sitting in the fields, one Save away from
               being claimed by the wrong tenant. */
            <SsoConnectionPanel key={selected} tenantId={selected} />
          ) : (
            tenants.length > 0 && (
              <Alert severity="info">
                Pick a tenant to view or edit its SSO connection.
              </Alert>
            )
          )}
        </>
      )}
    </Stack>
  );
}
